import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * Cart Drawer Component (hardened)
 * - Prevents duplicate listeners on Shopify Installments CTA across repeated open/close cycles
 * - Re-opens drawer after discount apply/remove (via sessionStorage flag)
 * - Promo apply/remove handled via event delegation (drawer-safe)
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {AbortController|null} */
  #installmentsAbortController = null;

  /** @type {AbortController|null} */
  #promoAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    this.#wirePromoHandlers();

    // Re-open after a discount apply/remove redirect
    try {
      const shouldOpen = window.sessionStorage.getItem('FJ_OPEN_CART_DRAWER') === '1';
      if (shouldOpen) {
        window.sessionStorage.removeItem('FJ_OPEN_CART_DRAWER');
        this.showDialog();
        this.#wireInstallmentsCtaClose();
      }
    } catch (e) {
      // ignore
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#cleanupInstallmentsListener();
    this.#cleanupPromoHandlers();
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
      this.#wireInstallmentsCtaClose();
    }
  };

  open() {
    this.showDialog();
    this.#wireInstallmentsCtaClose();
  }

  close() {
    this.#cleanupInstallmentsListener();
    this.closeDialog();
  }

  #cleanupInstallmentsListener() {
    if (this.#installmentsAbortController) {
      this.#installmentsAbortController.abort();
      this.#installmentsAbortController = null;
    }
  }

  async #wireInstallmentsCtaClose() {
    this.#cleanupInstallmentsListener();
    this.#installmentsAbortController = new AbortController();
    const { signal } = this.#installmentsAbortController;

    try {
      await customElements.whenDefined('shopify-payment-terms');
      if (signal.aborted) return;

      const termsEl =
        this.querySelector('shopify-payment-terms') ||
        document.querySelector('shopify-payment-terms');

      const shadow = termsEl?.shadowRoot || null;
      const cta = shadow?.querySelector('#shopify-installments-cta') || null;

      if (!cta) return;

      cta.addEventListener(
        'click',
        () => {
          this.closeDialog();
        },
        { once: true, signal }
      );
    } catch (e) {
      // Silent fail
    }
  }

  #cleanupPromoHandlers() {
    if (this.#promoAbortController) {
      this.#promoAbortController.abort();
      this.#promoAbortController = null;
    }
  }

  #setReopenFlag() {
    try {
      window.sessionStorage.setItem('FJ_OPEN_CART_DRAWER', '1');
    } catch (e) {
      // ignore
    }
  }

  #wirePromoHandlers() {
    this.#cleanupPromoHandlers();
    this.#promoAbortController = new AbortController();
    const { signal } = this.#promoAbortController;

    const getRedirectUrl = () => window.location.pathname + window.location.search;

    const cleanCode = (value) => {
      return (value || '').trim().replace(/\s+/g, '').toUpperCase();
    };

    document.addEventListener(
      'click',
      (e) => {
        const root = e.target.closest('[data-fj-cart-summary]');
        if (!root) return;

        const err = root.querySelector('[data-fj-promo-error]');
        const input = root.querySelector('[data-fj-promo-input]');
        const defaultCode = cleanCode(root.getAttribute('data-fj-default-code') || 'GIFT20');

        const clearErr = () => {
          if (!err) return;
          err.hidden = true;
          err.textContent = '';
        };

        const showErr = (msg) => {
          if (!err) return;
          err.hidden = false;
          err.textContent = msg;
        };

        // APPLY typed code
        const applyBtn = e.target.closest('[data-fj-promo-apply]');
        if (applyBtn) {
          clearErr();

          const code = cleanCode(input?.value || '');
          if (!code) {
            showErr('Please enter a code.');
            return;
          }

          this.#setReopenFlag();
          window.location.href =
            '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(getRedirectUrl());
          return;
        }

        // APPLY default GIFT20
        const applyDefault = e.target.closest('[data-fj-apply-default]');
        if (applyDefault) {
          clearErr();

          if (!defaultCode) {
            showErr('Promo code unavailable.');
            return;
          }

          this.#setReopenFlag();
          window.location.href =
            '/discount/' + encodeURIComponent(defaultCode) + '?redirect=' + encodeURIComponent(getRedirectUrl());
          return;
        }

        // REMOVE discount code
        const removeBtn = e.target.closest('[data-fj-promo-remove]');
        if (removeBtn) {
          clearErr();
          this.#setReopenFlag();
          window.location.href =
            '/discount/0?redirect=' + encodeURIComponent(getRedirectUrl());
        }
      },
      { signal }
    );

    document.addEventListener(
      'keydown',
      (e) => {
        const input = e.target.closest('[data-fj-promo-input]');
        if (!input) return;

        if (e.key === 'Enter') {
          e.preventDefault();
          const root = input.closest('[data-fj-cart-summary]');
          const btn = root?.querySelector('[data-fj-promo-apply]');
          if (btn) btn.click();
        }
      },
      { signal }
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}